import { useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import {
  CancelChangesModal,
  CertificateModalLoadingState,
  Typography,
  UpdateCertificateModal,
} from '@breezeai-frontend/cargo-ui';
import { type FormApi, useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { zodValidator } from '@tanstack/zod-form-adapter';

import {
  useAuthenticatedUser,
  useUserSetting,
} from '../../../../context/auth/auth-hooks';
import { type BffEditPolicyResponse } from '../../../../generated/api-client';
import { type SupportedCurrencies } from '../../../../model/CurrencyValue';
import { distributorQueries } from '../../../../network/apis/distributors/distributors';
import { type NetworkResponse } from '../../../../network/AuthRequests';
import { NoActiveOpenCoverModal } from '../../../components/NoActiveOpenCoverModal/NoActiveOpenCoverModal';
import { RouteDetails } from '../../../components/RouteDetails/RouteDetails';
import { RouteDetailsInputsProvider } from '../../../components/RouteDetails/RouteDetailsInputProvider';
import { getDefaultInputValue } from '../../../components/RouteDetails/utils';
import { useDefaultPrimaryMot } from '../../../hooks/useDefaultPrimaryMot';
import { useDefaultSecondaryMot } from '../../../hooks/useDefaultSecondaryMot';
import { policiesQueries, useUpdatePolicy } from '../../network/queries';
import { useModalStore } from '../useModalStore';
import {
  getDefaultFormData,
  usePolicyEditFormLogic,
} from '../usePolicyEditFormLogic';
import { ShipmentDetailsForm } from './ShipmentDetailsForm';
import { SubmissionButtons } from './SubmissionButtons';
import { type PolicyEditFormProps } from './types';

export function FormContent({
  form,
  isSubmitting,
}: {
  form: FormApi<PolicyEditFormProps, typeof zodValidator>;
  isSubmitting: boolean;
}) {
  return (
    <form
      className="flex flex-1 flex-col p-3 h-full bg-white w-full gap-3 rounded-3xl min-h-fit"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <Typography customStyles="text-md p-4 border-2 border-brand-wtw-secondary-500 bg-brand-wtw-secondary-50 rounded-lg">
        <strong>Note:</strong> Only some of the fields are editable after a
        certificate has been created.
      </Typography>
      <div className="flex flex-1 flex-col gap-3 md:flex-row">
        <div className="flex h-full bg-surfaces-secondary rounded-2xl p-5 min-w-96">
          <RouteDetails form={form} isDisabled={true} />
        </div>

        <div className="flex flex-col w-full">
          <ShipmentDetailsForm form={form} />
          <div className="flex flex-row gap-2 self-end mt-auto justify-end">
            <SubmissionButtons form={form} isSubmitting={isSubmitting} />
          </div>
        </div>
      </div>
    </form>
  );
}

// TODO: Component was copy-pasted from CertificateFlowPage - potentially refactor for less duplication
export function PolicyEditForm({ remountForm }: { remountForm: () => void }) {
  const { getPayload, policyId } = usePolicyEditFormLogic();

  const defaultPrimaryMot = useDefaultPrimaryMot();
  const defaultUserCurrency =
    useUserSetting<SupportedCurrencies>('default_currency');

  const navigate = useNavigate();

  const { data: policy } = useQuery(policiesQueries.details({ policyId }));

  const user = useAuthenticatedUser();
  const { data: openCover } = useQuery(
    distributorQueries.activeOpenCover({ distributorId: user.distributor.id }),
  );

  const [isOpenCoverModalOpen, setIsOpenCoverModalOpen] = useState(false);

  const {
    isCancelChangesModalOpen,
    setIsCancelChangesModalOpen,
    isConfirmChangesModalOpen,
    setIsConfirmChangesModalOpen,
  } = useModalStore();

  const form = useForm<PolicyEditFormProps, typeof zodValidator>({
    // Required fields need to have default values
    defaultValues: getDefaultFormData({
      policy: policy,
      defaultPrimaryMot,
      defaultLoadingMot: useDefaultSecondaryMot(
        policy?.quote?.loading_transport_mode_code,
      ),
      defaultDeliveryMot: useDefaultSecondaryMot(
        policy?.quote?.delivery_transport_mode_code,
      ),
      defaultUserCurrency,
      defaultCoveragePackage: policy?.quote?.coverage_package,
      defaultContainerMode: policy?.quote?.container_mode,
      defaultDistributorId: policy?.quote?.distributor_id,
    }),
    onSubmit: ({ formApi }) => {
      if (!openCover) {
        setIsOpenCoverModalOpen(true);
        return;
      }

      const payload = getPayload(formApi);
      return policyUpdateMutate(payload);
    },

    validatorAdapter: zodValidator,
  });

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      form.state.isDirty &&
      !form.state.isSubmitted &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const onMutationSuccess = (
    response: NetworkResponse<BffEditPolicyResponse>,
  ) => {
    navigate(`/policies/${response.data.policy_id}`, {
      replace: true,
    });

    // Reset the form state
    remountForm();
  };

  const {
    isPending: isPolicyUpdateMutating,
    isError: isPolicyUpdateMutatingError,
    mutate: policyUpdateMutate,
    reset: policyUpdateReset,
  } = useUpdatePolicy({
    policyId: policyId,
    options: {
      onSuccess: onMutationSuccess,
    },
  });

  return (
    <RouteDetailsInputsProvider
      // These are default values for the route details inputs
      originInputValue={getDefaultInputValue(form.getFieldValue('origin'))}
      destinationInputValue={getDefaultInputValue(
        form.getFieldValue('destination'),
      )}
      placeOfLoadingInputValue={
        form.getFieldValue('placeOfLoading')?.place?.description
      }
      placeOfDeliveryInputValue={
        form.getFieldValue('placeOfDelivery')?.place?.description
      }
    >
      <div className="flex flex-col w-full" data-testid="policy-edit-form">
        <FormContent form={form} isSubmitting={isPolicyUpdateMutating} />
      </div>
      <CertificateModalLoadingState
        isOpen={isPolicyUpdateMutating || isPolicyUpdateMutatingError}
        isError={isPolicyUpdateMutatingError}
        isLoading={isPolicyUpdateMutating}
        showExitButton={isPolicyUpdateMutatingError}
        onExit={() => {
          if (isPolicyUpdateMutatingError) {
            policyUpdateReset();
          }
        }}
      />
      <CancelChangesModal
        isOpen={isCancelChangesModalOpen || blocker.state === 'blocked'}
        onOpenChange={(isOpen) => {
          setIsCancelChangesModalOpen(isOpen);
          blocker.reset?.();
        }}
        onDiscardChanges={() => {
          void remountForm();
          blocker.proceed?.();
        }}
        title="Are you sure you want to cancel changes?"
        subtitle="Your changes will be lost."
      />
      <UpdateCertificateModal
        isOpen={isConfirmChangesModalOpen}
        onOpenChange={setIsConfirmChangesModalOpen}
        onConfirmChanges={() => void form.handleSubmit()}
      />
      <NoActiveOpenCoverModal
        isOpen={isOpenCoverModalOpen}
        onOpenChange={setIsOpenCoverModalOpen}
      />
    </RouteDetailsInputsProvider>
  );
}
